import type { AuthProvider } from "@refinedev/core";
import { axiosInstance } from "@/providers/data";
import { forgetEvidenceAccess } from "@/lib/evidence";
import { consumeLoginCode, sessionExists, signOut } from "@/lib/supertokens";

/**
 * Real auth against immersive-server's SuperTokens, replacing the demo
 * localStorage login this panel shipped with.
 *
 * There is no separate "admin user" store: the panel signs in a regular
 * `users` row by email OTP, and the server's `AdminGuard` admits it only if
 * that row's `role` is exactly `admin`. Since the role lives on the local user
 * row and is *not* stamped into the access-token payload, the panel cannot read
 * it from the token — so `login` probes one admin endpoint and refuses (and
 * signs back out) when the account is authenticated but not an admin. That
 * turns "every grid shows 403" into one honest message at the door.
 */
const ADMIN_PROBE_URL = "/api/v2/admin/story-views?page=1&limit=1";

export type LoginVariables = {
  email: string;
  /**
   * The 6-digit code. Sending the code is step one and is driven straight from
   * the login page (`sendLoginCode`) — `login` is only ever the step that turns
   * a code into a session, so Refine's mutation state maps 1:1 onto "did I get
   * signed in", with no half-authenticated success to encode.
   */
  code: string;
};

async function isAdmin(): Promise<boolean> {
  try {
    await axiosInstance.get(ADMIN_PROBE_URL);
    return true;
  } catch (error) {
    // The interceptor has already normalized this to Refine's HttpError shape.
    const status = (error as { statusCode?: number }).statusCode;
    if (status === 403 || status === 401) {
      return false;
    }
    // A server that is down or broken is not the same as "you are not an
    // admin" — let the caller through and let the resource pages report it.
    return true;
  }
}

export const authProvider: AuthProvider = {
  login: async ({ email, code }: LoginVariables) => {
    if (!code) {
      return {
        success: false,
        error: { name: "Sign-in failed", message: "Enter the code we emailed." },
      };
    }

    const outcome = await consumeLoginCode(code);
    if (outcome !== "ok") {
      const message =
        outcome === "incorrect"
          ? "That code is not right. Check it and try again."
          : outcome === "expired"
            ? "That code has expired. Send a new one."
            : "Login attempt expired. Start over with your email.";
      return { success: false, error: { name: "Sign-in failed", message } };
    }

    if (!(await isAdmin())) {
      await signOut();
      return {
        success: false,
        error: {
          name: "Not an admin",
          message: `${email} signed in, but does not have the admin role.`,
        },
      };
    }

    // The reviewer-access answer belongs to whoever was signed in before.
    forgetEvidenceAccess();
    return { success: true, redirectTo: "/" };
  },

  logout: async () => {
    await signOut();
    forgetEvidenceAccess();
    return { success: true, redirectTo: "/login" };
  },

  onError: async (error) => {
    // 401 means the session is gone or was never established — bounce to login.
    // 403 means the session is fine but the account lacks the admin role;
    // logging out there would hide the reason behind a login screen.
    if (error?.statusCode === 401 || error?.response?.status === 401) {
      return { logout: true, redirectTo: "/login" };
    }
    return { error };
  },

  check: async () => {
    if (await sessionExists()) {
      return { authenticated: true };
    }
    return {
      authenticated: false,
      error: { name: "No session", message: "Sign in to continue" },
      logout: true,
      redirectTo: "/login",
    };
  },

  getPermissions: async () => null,

  getIdentity: async () => {
    if (!(await sessionExists())) {
      return null;
    }
    try {
      const { data } = await axiosInstance.get("/api/v2/user/me");
      const profile = data?.data;
      if (!profile) {
        return null;
      }
      return {
        id: profile.id,
        name: profile.name ?? profile.email,
        email: profile.email,
        avatar: profile.avatarUrl ?? undefined,
      };
    } catch {
      return null;
    }
  },
};
